import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import { FC, createContext, useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Partner } from 'types';

import dividendsTemplates from 'constants/dividendsTemplates';

import successSwal from 'utils/successSwal';

import { setModal } from 'modules/_shared/redux/modalActions';
import { useGetOperationPartners } from 'modules/transactions/hooks/useGetOperationPartners';

import errorSwal from 'utils/errorSwal';
import { DividendDocumentModalContextType } from '../types/DividendDocumentModalContextType';
import { dividendDocumentModalContextInitValue } from '../constants/dividendDocumentModalContextInitValue';
import { generateDividendDocuments } from '../utils/generateDividendDocuments';

const DividendDocumentModalContext =
  createContext<DividendDocumentModalContextType>(
    dividendDocumentModalContextInitValue
  );

export const DividendDocumentModalProvider: FC<{
  children: React.ReactNode; // eslint-disable-line
  operationId: string | undefined;
}> = ({ children, operationId }) => {
  const dispatch = useDispatch();

  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society.actualSociety
  );

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [selectedPartners, setSelectedPartners] = useState<Partner[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    data: { partners, totalPartners },
    isLoading: isLoadingPartners,
  } = useGetOperationPartners({
    operationId,
    page: currentPage,
  });

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangePage = (page: number) => {
    setCurrentPage(page);
  };

  const handleChangeRowsPerPage = (rowsPerPage: number) => {
    setRowsPerPage(rowsPerPage);
  };

  const handleSelectPartner = (partner: Partner) => {
    setSelectedPartners((prev) => {
      if (prev.some((p) => p._id === partner._id)) {
        return prev.filter((p) => p._id !== partner._id);
      }
      return [...prev, partner];
    });
  };

  const handleGenerateDocument = async () => {
    try {
      setIsLoading(true);

      const societyTemplate = actualSociety?.templates?.dividends;

      const templateId =
        societyTemplate ||
        dividendsTemplates[
          process.env.NODE_ENV as 'development' | 'production'
        ];

      await generateDividendDocuments({
        selectedPartners,
        templateId,
        operationId,
        societyId: actualSociety?._id,
      });

      setIsLoading(false);

      successSwal(t('LettersGeneratedSuccessfully'));

      handleCloseModal();
    } catch (error) {
      errorSwal(t('ErrorGeneratingLetters'));
      setIsLoading(false);
    }
  };

  return (
    <DividendDocumentModalContext.Provider
      value={{
        data: {
          operationId,
          partners,
          totalPartners,
          selectedPartners,
          currentPage,
          rowsPerPage,
          isLoading: isLoading || isLoadingPartners,
        },
        actions: {
          handleCloseModal,
          handleChangePage,
          handleChangeRowsPerPage,
          handleSelectPartner,
          handleGenerateDocument,
        },
      }}
    >
      {children}
    </DividendDocumentModalContext.Provider>
  );
};

export const useDividendDocumentModal = () => {
  const context = useContext(DividendDocumentModalContext);
  if (!context) {
    throw new Error(
      'useDividendDocumentModal must be used within a DividendDocumentModalProvider'
    );
  }
  return context;
};
